import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, Dispatch, SetStateAction } from "react";
import moment from "moment";
import {
  ChevronDown,
  Eye,
  MoreVertical,
  Pencil,
  Pin,
  PinOff,
  Plus,
  Trash,
  X,
} from "lucide-react";
import { toast } from "sonner";
import AxiosInstance from "@/utils/AxiosInstance";
import { formatDateTime } from "@/utils/helpers";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { InternalNoteSaveResult } from "@/components/PatientInternalNoteDialog";
import type { InternalNoteV2 } from "../types";
import { PatientV2CircularLoader } from "../shared/PatientV2CircularLoader";
import { AddInternalNoteDialog } from "./AddInternalNoteDialog";

type Props = {
  patient: Record<string, unknown> | null;
  patientId: string;
  notes: InternalNoteV2[];
  setNotes: Dispatch<SetStateAction<InternalNoteV2[]>>;
  authorName: string;
  userId?: string;
  isAdmin?: boolean;
  isPractitioner?: boolean;
  isCaretaker?: boolean;
  canAddOrEditInternalNotes?: boolean;
  canAddNursingNote?: boolean;
  onAddNursingNote?: () => void;
};

type PinAction = {
  noteId: string;
  note?: InternalNoteV2;
  action: "pin" | "unpin";
};

type DateNoteGroup = {
  dateKey: string;
  label: string;
  notes: InternalNoteV2[];
};

function getInternalNoteCreatorId(note: InternalNoteV2): string | undefined {
  const createdBy = note.createdBy as
    | { _id?: string; id?: string; userId?: string; caretakerId?: string }
    | string
    | undefined;

  if (createdBy && typeof createdBy === "object") {
    return createdBy._id ?? createdBy.id ?? createdBy.userId ?? createdBy.caretakerId;
  }

  return (
    (typeof createdBy === "string" ? createdBy : undefined) ??
    note.authorId ??
    note.userId
  );
}

function isOwnInternalNote(note: InternalNoteV2, userId: string | undefined): boolean {
  if (!userId) return false;
  const creatorId = getInternalNoteCreatorId(note);
  return creatorId != null && String(creatorId) === String(userId);
}

function canManageNote(note: InternalNoteV2, userId: string | undefined, _isAdmin: boolean) {
  return isOwnInternalNote(note, userId);
}

function getNoteTimestamp(note: InternalNoteV2) {
  return (note as { createdAtLocal?: string }).createdAtLocal ?? note.createdAt;
}

function getInternalNoteAuthorName(note: InternalNoteV2): string {
  const createdBy = note.createdBy as
    | { firstName?: string; lastName?: string; name?: string; fullName?: string }
    | string
    | undefined;

  if (createdBy && typeof createdBy === "object") {
    const fullName =
      [createdBy.firstName, createdBy.lastName].filter(Boolean).join(" ") ||
      createdBy.name ||
      createdBy.fullName;
    if (fullName) return fullName;
  }

  return String(note.authorName || "Unknown");
}

function isNotePinned(note: InternalNoteV2): boolean {
  return !!(note.isPinned || (note as { pinned?: boolean }).pinned);
}

function formatNoteTimeOnly(date: Date | string | number | null | undefined): string {
  if (!date) return "";
  const m = moment(date).local();
  return m.isValid() ? m.format("h:mm A") : "";
}

function getDateGroupLabel(date: Date | string | number | null | undefined): string {
  if (!date) return "";
  const noteDay = moment(date).local().startOf("day");
  if (!noteDay.isValid()) return "";

  const today = moment().startOf("day");
  if (noteDay.isSame(today, "day")) return "Today";
  if (noteDay.isSame(today.clone().subtract(1, "day"), "day")) return "Yesterday";

  return noteDay.format("MMMM D, YYYY");
}

function groupNotesByDate(notes: InternalNoteV2[]): DateNoteGroup[] {
  const groups: DateNoteGroup[] = [];

  for (const note of notes) {
    const raw = getNoteTimestamp(note);
    const dateKey = moment(raw).local().format("YYYY-MM-DD");
    const label = getDateGroupLabel(raw);
    const last = groups[groups.length - 1];

    if (last && last.dateKey === dateKey) {
      last.notes.push(note);
    } else {
      groups.push({ dateKey, label, notes: [note] });
    }
  }

  return groups;
}

function previewText(value: string) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

function ClampedText({
  value,
  textClassName,
  lines = 2,
}: {
  value: string;
  textClassName: string;
  lines?: number;
}) {
  const text = previewText(value);
  const clampStyle: CSSProperties =
    lines <= 1
      ? {}
      : {
          display: "-webkit-box",
          WebkitLineClamp: lines,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        };

  return (
    <div className="relative flex min-w-0 items-start">
      <p
        className={`min-w-0 flex-1 wrap-break-word ${lines <= 1 ? "truncate" : ""} ${textClassName}`}
        style={clampStyle}
      >
        {text}
      </p>
    </div>
  );
}

function DateDivider({ label }: { label: string }) {
  return (
    <div className="flex justify-center py-2">
      <span className="rounded-lg bg-[#E1F2FB] px-3 py-1 text-[11px] font-medium text-[#54656f] shadow-sm">
        {label}
      </span>
    </div>
  );
}

function InternalNoteBubble({
  note,
  authorName,
  timeLabel,
  isOwnNote,
  canPin,
  showManageActions,
  inPinnedSection = false,
  onView,
  onEdit,
  onDelete,
  onPin,
  onUnpin,
}: {
  note: InternalNoteV2;
  authorName: string;
  timeLabel: string;
  isOwnNote: boolean;
  canPin: boolean;
  showManageActions: boolean;
  inPinnedSection?: boolean;
  onView: (note: InternalNoteV2) => void;
  onEdit: (note: InternalNoteV2) => void;
  onDelete: (note: InternalNoteV2) => void;
  onPin: (note: InternalNoteV2) => void;
  onUnpin: (note: InternalNoteV2) => void;
}) {
  const isPinned = isNotePinned(note);

  return (
    <div
      className={`flex ${isOwnNote ? "justify-end" : "justify-start"} ${
        inPinnedSection ? "" : "pb-1"
      }`}
    >
      <div
        className={`group relative w-fit max-w-[92%] rounded-lg px-3 py-2 shadow-sm ${
          isOwnNote
            ? "bg-[#DCF8C6] text-black"
            : "border border-gray-200 bg-white text-black"
        }`}
      >
        <div
          className={`absolute top-2 h-0 w-0 border-y-[6px] border-y-transparent ${
            isOwnNote
              ? "-right-1 border-l-8 border-l-[#DCF8C6]"
              : "-left-1 border-r-8 border-r-white"
          }`}
        />

        <div className="mb-1 flex items-center justify-between gap-2">
          <p className="teachers text-[11px] font-medium leading-none text-[#667781]">
            {authorName}
          </p>

          <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  size="sm"
                  className="h-6 w-6 p-0 bg-transparent text-[#667781] opacity-70 hover:bg-black/5 group-hover:opacity-100"
                  aria-label="Note actions"
                >
                  <MoreVertical className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem onClick={() => onView(note)}>
                  <Eye className="h-4 w-4" />
                  View
                </DropdownMenuItem>
                {showManageActions ? (
                  <>
                    <DropdownMenuItem onClick={() => onEdit(note)}>
                      <Pencil className="h-4 w-4" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      variant="destructive"
                      onClick={() => onDelete(note)}
                    >
                      <Trash className="h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </>
                ) : null}
                {canPin ? (
                  <>
                    {showManageActions ? <DropdownMenuSeparator /> : null}
                    <DropdownMenuItem
                      onClick={() => (isPinned ? onUnpin(note) : onPin(note))}
                    >
                      {isPinned ? (
                        <PinOff className="h-4 w-4" />
                      ) : (
                        <Pin className="h-4 w-4" />
                      )}
                      {isPinned ? "Unpin" : "Pin"}
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
        </div>

        <div className="space-y-0.5">
          <ClampedText
            value={String(note.subject ?? "")}
            textClassName="teachers text-sm font-semibold leading-snug"
            lines={2}
          />
          <ClampedText
            value={String(note.content ?? "")}
            textClassName="teachers text-sm font-normal leading-snug text-[#303030]"
            lines={3}
          />
        </div>

        <div className="mt-1 flex justify-end">
          <p className="teachers text-[10px] text-[#667781]">{timeLabel}</p>
        </div>
      </div>
    </div>
  );
}

export function InternalNotesPanel({
  patient,
  patientId,
  notes,
  setNotes,
  authorName,
  userId,
  isAdmin = false,
  isPractitioner = false,
  isCaretaker: _isCaretaker = false,
  canAddOrEditInternalNotes = true,
  canAddNursingNote = false,
  onAddNursingNote,
}: Props) {
  console.log("Patient Tab 3B -------------------------------------------------------------------------------");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<InternalNoteV2 | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [notesOpen, setNotesOpen] = useState(true);
  const [showOnlyMyNotes, setShowOnlyMyNotes] = useState(false);
  const [search, setSearch] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewNote, setPreviewNote] = useState<InternalNoteV2 | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<InternalNoteV2 | null>(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [unpinOpen, setUnpinOpen] = useState(false);
  const [pendingPin, setPendingPin] = useState<InternalNoteV2 | null>(null);
  const lastSearchRef = useRef<string>("");

  const enrollmentAllowsAdd = patient?.enrollmentStatus !== "pending";
  const canAddInternalNote = canAddOrEditInternalNotes && enrollmentAllowsAdd && !isPractitioner;
  const showAddNoteMenu = canAddInternalNote || canAddNursingNote;

  const isOwnNoteForUser = useCallback(
    (note: InternalNoteV2) => isOwnInternalNote(note, userId),
    [userId],
  );

  const canManageNoteForUser = useCallback(
    (note: InternalNoteV2) => canManageNote(note, userId, isAdmin),
    [userId, isAdmin],
  );

  const refetchInternalNotes = useCallback(async () => {
    if (!patientId) return;
    try {
      setSearchLoading(true);
      const { data }: any = await AxiosInstance().get("/patients/internal-note", {
        params: {
          patientId,
          search: lastSearchRef.current || undefined,
        },
      });
      setNotes(Array.isArray(data?.data?.notes) ? data.data.notes : []);
    } catch {
      toast.error("Failed to refresh internal notes");
    } finally {
      setSearchLoading(false);
    }
  }, [patientId, setNotes]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const normalized = search.trim();
      if (normalized === lastSearchRef.current) return;
      lastSearchRef.current = normalized;
      void refetchInternalNotes();
    }, 500);
    return () => window.clearTimeout(timer);
  }, [search, refetchInternalNotes]);

  const handleTogglePinInternalNote = async ({ noteId, action }: PinAction) => {
    if (!noteId) return;
    try {
      setSearchLoading(true);
      await AxiosInstance().post(`/patients/internal-note/${noteId}/${action}`);
      await refetchInternalNotes();
      toast.success(action === "pin" ? "Note pinned" : "Note unpinned");
    } catch {
      toast.error("Failed to update pin status");
      setSearchLoading(false);
    }
  };

  const handleDeleteNotes = async (noteId: string) => {
    if (!noteId) return;
    try {
      await AxiosInstance().delete(`/patients/internal-note/${noteId}`);
      setNotes((prev) => prev.filter((item) => item._id !== noteId));
      toast.success("Note deleted");
    } catch {
      toast.error("Failed to delete note");
    }
  };

  // const onSaved = (result: InternalNoteSaveResult) => {
  //   if (result.type === "created") {
  //     const createdNote =
  //       (result.apiNote as InternalNoteV2 | null) ?? ({
  //         _id: String(Date.now()),
  //         subject: "",
  //         content: "",
  //         createdAt: new Date().toISOString(),
  //         authorName,
  //       } as InternalNoteV2);
  //     setNotes((prev) => [createdNote, ...prev]);
  //     return;
  //   }
  //   setNotes((prev) =>
  //     prev.map((note) =>
  //       note._id === result.noteId
  //         ? { ...note, subject: result.subject, content: result.content }
  //         : note,
  //     ),
  //   );
  // };

  const onSaved = (_result: InternalNoteSaveResult) => {
    // alert("onSaved");
    void refetchInternalNotes();
  };

  const filteredNotes = useMemo(() => {
    const base = showOnlyMyNotes
      ? (notes || []).filter((note) => isOwnNoteForUser(note))
      : notes || [];
    return base;
  }, [notes, showOnlyMyNotes, isOwnNoteForUser]);

  const pinnedNotes = useMemo(
    () => filteredNotes.filter((note) => isNotePinned(note)),
    [filteredNotes],
  );

  const unpinnedNotes = useMemo(
    () => filteredNotes.filter((note) => !isNotePinned(note)),
    [filteredNotes],
  );

  const unpinnedNoteGroups = useMemo(
    () => groupNotesByDate(unpinnedNotes),
    [unpinnedNotes],
  );

  const openPinDialog = (note: InternalNoteV2) => {
    setPendingPin(note);
    if (isNotePinned(note)) setUnpinOpen(true);
    else setPinOpen(true);
  };

  const confirmPin = () => {
    if (!pendingPin?._id) return;
    void handleTogglePinInternalNote({
      noteId: String(pendingPin._id),
      note: pendingPin,
      action: "pin",
    });
    setPinOpen(false);
    setPendingPin(null);
  };

  const confirmUnpin = () => {
    if (!pendingPin?._id) return;
    void handleTogglePinInternalNote({
      noteId: String(pendingPin._id),
      note: pendingPin,
      action: "unpin",
    });
    setUnpinOpen(false);
    setPendingPin(null);
  };

  const confirmDelete = () => {
    if (!noteToDelete?._id) return;
    void handleDeleteNotes(String(noteToDelete._id));
    setDeleteOpen(false);
    setNoteToDelete(null);
  };

  const renderNote = (note: InternalNoteV2, inPinnedSection = false) => {
    const isOwn = isOwnNoteForUser(note);
    const showManageActions = isOwn && (inPinnedSection || !isPractitioner);

    return (
      <InternalNoteBubble
        key={String(note._id)}
        note={note}
        authorName={getInternalNoteAuthorName(note)}
        timeLabel={formatNoteTimeOnly(getNoteTimestamp(note))}
        isOwnNote={isOwn}
        canPin={isOwn && !isPractitioner}
        showManageActions={showManageActions}
        inPinnedSection={inPinnedSection}
        onView={(item) => {
          setPreviewNote(item);
          setPreviewOpen(true);
        }}
        onEdit={(item) => {
          setEditingNote(item);
          setDialogOpen(true);
        }}
        onDelete={(item) => {
          setNoteToDelete(item);
          setDeleteOpen(true);
        }}
        onPin={openPinDialog}
        onUnpin={openPinDialog}
      />
    );
  };

  return (
    <div className="teachers flex h-full min-h-0 flex-col p-2">
      <div className="flex w-full shrink-0 items-center gap-2 py-2">
        <button
          type="button"
          onClick={() => setNotesOpen((prev) => !prev)}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
              notesOpen ? "rotate-0" : "-rotate-90"
            }`}
          />
          <span className="text-sm font-semibold">Internal Notes</span>
        </button>

        <div className="flex shrink-0 items-center gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="inline-flex items-center">
                  <Switch
                    checked={showOnlyMyNotes}
                    onCheckedChange={setShowOnlyMyNotes}
                    id="my-notes-filter"
                    className="origin-center translate-y-px scale-90"
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                {showOnlyMyNotes ? "Showing only my notes" : "Showing all notes"}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {showAddNoteMenu ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="h-7.5 whitespace-nowrap">
                  <Plus className="h-3 w-3" />
                  Add Note
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={!canAddInternalNote}
                  onSelect={() => {
                    if (!canAddInternalNote) return;
                    setEditingNote(null);
                    setDialogOpen(true);
                  }}
                >
                  Internal Note
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={!canAddNursingNote}
                  onSelect={() => {
                    if (!canAddNursingNote) return;
                    onAddNursingNote?.();
                  }}
                >
                  Nursing Note
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      <div
        className={notesOpen ? "flex min-h-0 flex-1 flex-col overflow-hidden" : "hidden"}
        aria-hidden={!notesOpen}
      >
        <div className="teachers relative flex h-full min-h-0 max-h-full flex-col overflow-hidden rounded-[5px] border-[0.3px] border-grey">
          <div className="sticky top-0 z-10 shrink-0 bg-white">
            <div className="p-3">
              <div className="relative">
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search notes..."
                  className="pr-9"
                />
                {search.length > 0 ? (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>
            </div>
            <Separator />
          </div>

          <div className="thin-invisible-scrollbar min-h-0 flex-1 overflow-y-auto p-3 pr-2  pt-0">
            <div className="h-full space-y-1">
              {searchLoading ? (
                <PatientV2CircularLoader size="sm" className="h-full p-4" />
              ) : filteredNotes.length === 0 ? (
                <p className="text-sm text-tertiary">No internal notes available yet.</p>
              ) : (
                <div className="mb-0 pt-3">
                  {pinnedNotes.length > 0 ? (
                    <div className="sticky top-0 z-10 bg-white/95 backdrop-blur supports-backdrop-filter:bg-white/80">
                      <DateDivider label="Pinned" />
                      <div className="space-y-1">
                        {pinnedNotes.map((note) => renderNote(note, true))}
                      </div>
                      <div className="pt-2" />
                    </div>
                  ) : null}

                  {unpinnedNoteGroups.map((group) => (
                    <div key={group.dateKey} className="space-y-1">
                      <DateDivider label={group.label} />
                      {group.notes.map((note) => renderNote(note, false))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <Dialog
            open={previewOpen}
            onOpenChange={(open) => {
              setPreviewOpen(open);
              if (!open) setPreviewNote(null);
            }}
          >
            <DialogContent className="teachers flex max-h-[min(90vh,600px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
              <DialogHeader className="shrink-0 border-b px-5 py-4 text-left">
                <DialogTitle className="text-base">Internal Note</DialogTitle>
              </DialogHeader>
              <div className="min-h-0 space-y-4 overflow-y-auto px-5 py-4">
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                    Subject
                  </p>
                  <p className="text-sm font-semibold wrap-break-word">
                    {String(previewNote?.subject || "").trim() || "—"}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wide">
                    Note
                  </p>
                  <p className="text-sm whitespace-pre-wrap wrap-break-word">
                    {String(previewNote?.content || "").trim() || "—"}
                  </p>
                </div>
                <p className="text-muted-foreground text-xs">
                  {previewNote
                    ? `${getInternalNoteAuthorName(previewNote)} • ${formatDateTime(
                        getNoteTimestamp(previewNote),
                      )}`
                    : ""}
                </p>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
            <DialogContent className="teachers sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Delete note</DialogTitle>
              </DialogHeader>
              <p className="text-muted-foreground text-sm">
                Are you sure you want to delete this internal note?
              </p>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDeleteOpen(false)}>
                  Cancel
                </Button>
                <Button type="button" variant="destructive" onClick={confirmDelete}>
                  Delete
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog
            open={pinOpen}
            onOpenChange={(open) => {
              setPinOpen(open);
              if (!open) setPendingPin(null);
            }}
          >
            <DialogContent className="teachers sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Pin note?</DialogTitle>
              </DialogHeader>
              <p className="text-muted-foreground text-sm">
                Pin this internal note? If another note is already pinned, it will be unpinned.
              </p>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setPinOpen(false)}>
                  No
                </Button>
                <Button type="button" onClick={confirmPin}>
                  Yes
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog
            open={unpinOpen}
            onOpenChange={(open) => {
              setUnpinOpen(open);
              if (!open) setPendingPin(null);
            }}
          >
            <DialogContent className="teachers sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Unpin note?</DialogTitle>
              </DialogHeader>
              <p className="text-muted-foreground text-sm">Unpin this internal note?</p>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setUnpinOpen(false)}>
                  No
                </Button>
                <Button type="button" onClick={confirmUnpin}>
                  Yes
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <AddInternalNoteDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        patientId={patientId}
        noteToEdit={editingNote}
        authorName={authorName}
        canManageNote={canManageNoteForUser}
        onSaved={onSaved}
      />
    </div>
  );
}

export const InternalNotesPanelMemo = React.memo(InternalNotesPanel);
